<script>
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import PromptsAPI from 'dashboard/api/prompts';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const BUILT_IN_TEMPLATES = ['transcript', 'confirmation'];

export default {
  name: 'EmailTemplatesIndex',
  components: {
    BaseSettingsHeader,
    SettingsLayout,
    NextButton,
    NextInput,
    Dialog,
  },
  setup() {
    const { t } = useI18n();
    const { accountScopedRoute } = useAccount();
    return { t, accountScopedRoute };
  },
  data() {
    return {
      builtInTemplates: [
        {
          name: 'transcript',
          title: this.$t('EMAIL_TEMPLATES.TRANSCRIPT.TITLE'),
          description: this.$t('EMAIL_TEMPLATES.TRANSCRIPT.DESCRIPTION'),
          icon: 'i-lucide-file-text',
          builtIn: true,
        },
        {
          name: 'confirmation',
          title: this.$t('EMAIL_TEMPLATES.CONFIRMATION.TITLE'),
          description: this.$t('EMAIL_TEMPLATES.CONFIRMATION.DESCRIPTION'),
          icon: 'i-lucide-mail-check',
          builtIn: true,
        },
      ],
      customTemplates: [],
      isLoading: false,
      showCreateDialog: false,
      newTemplateName: '',
      isCreating: false,
      deleteTarget: null,
      showDeleteDialog: false,
      isDeleting: false,
    };
  },
  computed: {
    allTemplates() {
      const custom = this.customTemplates.map(name => ({
        name,
        title: name,
        description: this.$t('EMAIL_TEMPLATES.CUSTOM.DESCRIPTION'),
        icon: 'i-lucide-file-plus',
        builtIn: false,
      }));
      return [...this.builtInTemplates, ...custom];
    },
    isCreateDisabled() {
      return !this.newTemplateName.trim() || this.isCreating;
    },
  },
  mounted() {
    this.loadCustomTemplates();
  },
  methods: {
    async loadCustomTemplates() {
      this.isLoading = true;
      try {
        const response = await PromptsAPI.getCustomTemplates();
        this.customTemplates = response.data || [];
      } catch {
        // Custom templates not available, keep empty
      } finally {
        this.isLoading = false;
      }
    },
    openCreateDialog() {
      this.newTemplateName = '';
      this.showCreateDialog = true;
      this.$nextTick(() => this.$refs.createDialog?.open());
    },
    closeCreateDialog() {
      this.showCreateDialog = false;
    },
    async createTemplate() {
      const name = this.newTemplateName.trim();
      if (!name) return;

      const reservedNames = [...BUILT_IN_TEMPLATES, ...this.customTemplates];
      if (reservedNames.includes(name)) {
        useAlert(this.$t('EMAIL_TEMPLATES.TEMPLATE_NAME_EXISTS'));
        return;
      }

      this.isCreating = true;
      try {
        const defaultSummarization =
          await PromptsAPI.getDefault('summarization');
        const defaultTemplate = await PromptsAPI.getDefault('template');

        await PromptsAPI.create({
          name,
          type: 'summarization',
          content: defaultSummarization.data.content,
        });
        await PromptsAPI.create({
          name,
          type: 'template',
          content: defaultTemplate.data.content,
        });

        this.customTemplates.push(name);
        this.closeCreateDialog();
        useAlert(this.$t('EMAIL_TEMPLATES.CREATE_SUCCESS'));

        this.$router.push(
          this.accountScopedRoute('email_templates_edit', {
            templateName: name,
          })
        );
      } catch {
        useAlert(this.$t('EMAIL_TEMPLATES.CREATE_ERROR'));
      } finally {
        this.isCreating = false;
      }
    },
    confirmDelete(template) {
      this.deleteTarget = template;
      this.showDeleteDialog = true;
      this.$nextTick(() => this.$refs.deleteDialog?.open());
    },
    closeDeleteDialog() {
      this.showDeleteDialog = false;
      this.deleteTarget = null;
    },
    async deleteTemplate() {
      if (!this.deleteTarget) return;

      this.isDeleting = true;
      try {
        // Find and delete all prompts with this name
        const response = await PromptsAPI.get(null);
        const prompts = (response.data || []).filter(
          p => p.name === this.deleteTarget.name
        );

        for (const prompt of prompts) {
          await PromptsAPI.delete(prompt.id);
        }

        this.customTemplates = this.customTemplates.filter(
          name => name !== this.deleteTarget.name
        );
        this.closeDeleteDialog();
        useAlert(this.$t('EMAIL_TEMPLATES.DELETE_SUCCESS'));
      } catch (error) {
        const message =
          error?.response?.status === 422
            ? this.$t('EMAIL_TEMPLATES.DELETE_IN_USE_ERROR')
            : this.$t('EMAIL_TEMPLATES.DELETE_ERROR');
        useAlert(message);
      } finally {
        this.isDeleting = false;
      }
    },
  },
};
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="$t('EMAIL_TEMPLATES.TITLE')"
        :description="$t('EMAIL_TEMPLATES.DESCRIPTION')"
      />
    </template>
    <template #body>
      <div class="mb-4">
        <NextButton blue icon="i-lucide-plus" @click="openCreateDialog">
          {{ $t('EMAIL_TEMPLATES.CREATE_TEMPLATE') }}
        </NextButton>
      </div>
      <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <div
          v-for="template in allTemplates"
          :key="template.name"
          class="flex flex-col flex-1 p-6 m-[1px] outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow"
        >
          <div
            class="p-3 rounded-lg bg-slate-50 dark:bg-slate-700 w-fit w-12 h-12 mb-4"
          >
            <span
              :class="template.icon"
              class="size-6 text-slate-700 dark:text-slate-300"
            />
          </div>
          <h3
            class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2"
          >
            {{ template.title }}
          </h3>
          <p class="text-sm text-slate-600 dark:text-slate-400 mb-6 flex-grow">
            {{ template.description }}
          </p>
          <div class="flex gap-2">
            <NextButton
              blue
              icon-right="i-lucide-arrow-right"
              @click="
                $router.push(
                  accountScopedRoute('email_templates_edit', {
                    templateName: template.name,
                  })
                )
              "
            >
              {{ $t('EMAIL_TEMPLATES.CONFIGURE') }}
            </NextButton>
            <NextButton
              v-if="!template.builtIn"
              variant="outline"
              ruby
              icon="i-lucide-trash-2"
              @click="confirmDelete(template)"
            />
          </div>
        </div>
      </div>

      <!-- Create Template Dialog -->
      <Dialog
        v-if="showCreateDialog"
        ref="createDialog"
        :title="$t('EMAIL_TEMPLATES.CREATE_TEMPLATE')"
        :show-confirm-button="false"
        :show-cancel-button="false"
        @close="closeCreateDialog"
      >
        <div class="flex flex-col gap-4">
          <NextInput
            v-model="newTemplateName"
            :label="$t('EMAIL_TEMPLATES.TEMPLATE_NAME')"
            :placeholder="$t('EMAIL_TEMPLATES.TEMPLATE_NAME_PLACEHOLDER')"
            @keyup.enter="createTemplate"
          />
          <div class="flex justify-end gap-2">
            <NextButton faded slate @click="closeCreateDialog">
              {{ $t('AUTOMATION.ADD.CANCEL_BUTTON_TEXT') }}
            </NextButton>
            <NextButton
              blue
              :loading="isCreating"
              :disabled="isCreateDisabled"
              @click="createTemplate"
            >
              {{ $t('EMAIL_TEMPLATES.CREATE_TEMPLATE') }}
            </NextButton>
          </div>
        </div>
      </Dialog>

      <!-- Delete Confirmation Dialog -->
      <Dialog
        v-if="showDeleteDialog"
        ref="deleteDialog"
        :title="$t('EMAIL_TEMPLATES.DELETE_TEMPLATE')"
        :show-confirm-button="false"
        :show-cancel-button="false"
        @close="closeDeleteDialog"
      >
        <div class="flex flex-col gap-4">
          <p class="text-sm text-slate-600 dark:text-slate-400">
            {{ $t('EMAIL_TEMPLATES.DELETE_CONFIRM') }}
          </p>
          <div class="flex justify-end gap-2">
            <NextButton faded slate @click="closeDeleteDialog">
              {{ $t('AUTOMATION.ADD.CANCEL_BUTTON_TEXT') }}
            </NextButton>
            <NextButton ruby :loading="isDeleting" @click="deleteTemplate">
              {{ $t('EMAIL_TEMPLATES.DELETE_TEMPLATE') }}
            </NextButton>
          </div>
        </div>
      </Dialog>
    </template>
  </SettingsLayout>
</template>
